import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { supabase, SUPABASE_URL } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

/**
 * Static data for a site that rarely changes
 * Cached for long periods to reduce API calls
 */
export interface SiteStaticData {
  site: {
    id: string;
    name: string;
    location: string | null;
    status: string;
    start_date: string | null;
    end_date: string | null;
    budget_initial: number | null;
    company_id: string;
    site_manager_id: string;
  } | null;
  buildings: Array<{
    name: string;
    levels: string[];
  }>;
  workItems: Array<{
    id: string;
    name: string;
    building: string;
    level: string;
    volume: number;
    display_order: number | null;
    start_date: string | null;
    estimated_end_date: string | null;
    formula_id: string | null;
  }>;
  formulas: Array<{
    id: string;
    name: string;
    strength: string | null;
    exposure_class: string | null;
    consistency: string | null;
    granulometry: string | null;
    unit_price_m3: number;
  }>;
  suppliers: Array<{
    id: string;
    name: string;
    email: string | null;
    phone: string | null;
    contact_name: string | null;
    plant_phone: string | null;
  }>;
  plants: Array<{
    id: string;
    supplier_id: string;
    name: string;
    city: string;
    address: string | null;
    email: string;
    phone: string | null;
  }>;
  concreteOptions: Array<{
    id: string;
    name: string;
    category: string;
    unit_type: string;
    price: number;
    active: boolean;
  }>;
  workItemsCount: number;
  cached?: boolean;
}

// ========== LOOKUP MAPS FOR O(1) ACCESS ==========

export interface StaticDataMaps {
  formulasById: Map<string, SiteStaticData['formulas'][0]>;
  workItemsById: Map<string, SiteStaticData['workItems'][0]>;
  plantsById: Map<string, SiteStaticData['plants'][0]>;
  suppliersById: Map<string, SiteStaticData['suppliers'][0]>;
}

function buildLookupMaps(data: SiteStaticData | undefined): StaticDataMaps {
  const formulasById = new Map<string, SiteStaticData['formulas'][0]>();
  const workItemsById = new Map<string, SiteStaticData['workItems'][0]>();
  const plantsById = new Map<string, SiteStaticData['plants'][0]>();
  const suppliersById = new Map<string, SiteStaticData['suppliers'][0]>();
  
  if (data) {
    data.formulas.forEach(f => formulasById.set(f.id, f));
    data.workItems.forEach(w => workItemsById.set(w.id, w));
    data.plants.forEach(p => plantsById.set(p.id, p));
    data.suppliers.forEach(s => suppliersById.set(s.id, s));
  }
  
  return { formulasById, workItemsById, plantsById, suppliersById };
}

/**
 * Fetch static site data from Edge Function (preferred) or direct Supabase (fallback)
 */
async function fetchSiteStaticDataFromEdge(siteId: string, bypassCache = false): Promise<SiteStaticData> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    throw new Error("No active session");
  }

  try {
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/get-site-static-data`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
          // Bypass browser cache when server cache is bypassed
          ...(bypassCache ? { "Cache-Control": "no-cache" } : {}),
        },
        body: JSON.stringify({ siteId, bypassCache }),
      }
    );

    if (!response.ok) {
      throw new Error(`Edge function error: ${response.status}`);
    }

    const data = await response.json();
    return data as SiteStaticData;
  } catch (error) {
    logger.warn("useSiteStaticData", "Edge function failed, falling back to direct queries:", error);
    return fetchSiteStaticDataDirectly(siteId);
  }
}

/**
 * Invalidate the server-side cache for a site and refetch fresh data
 * Call this after saving previsionnel or other static data
 * Returns true if successful, false if failed (caller should use fallback)
 */
export async function invalidateSiteStaticCache(siteId: string): Promise<boolean> {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session?.access_token) {
    logger.warn("useSiteStaticData", "No active session, skipping cache invalidation");
    return false;
  }

  // Retry up to 2 times with timeout
  for (let attempt = 1; attempt <= 2; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);

    try {
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/get-site-static-data`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${session.access_token}`,
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify({ siteId, bypassCache: true }),
          signal: controller.signal,
        }
      );
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        return true;
      }
      
      logger.warn("useSiteStaticData", `Attempt ${attempt} returned status ${response.status}`);
    } catch (error) {
      clearTimeout(timeoutId);
      logger.warn("useSiteStaticData", `Attempt ${attempt} failed:`, error);
      
      if (attempt < 2) {
        // Small delay before retry
        await new Promise(r => setTimeout(r, 500));
      }
    }
  }
  
  logger.warn("useSiteStaticData", "All attempts failed, caller should use fallback");
  return false;
}

/**
 * Fallback: Fetch static site data directly from Supabase
 */
async function fetchSiteStaticDataDirectly(siteId: string): Promise<SiteStaticData> {
  // Parallel queries for all static data
  const [siteResult, workItemsResult, globalOrderResult] = await Promise.all([
    // Site basic info with company_id and site_manager_id
    supabase
      .from("sites")
      .select("id, name, location, status, start_date, end_date, budget_initial, company_id, site_manager_id")
      .eq("id", siteId)
      .single(),
    
    // Work items (structure only)
    supabase
      .from("work_items")
      .select("id, name, building, level, volume, display_order, start_date, estimated_end_date")
      .eq("site_id", siteId)
      .is("deleted_at", null)
      .order("display_order", { ascending: true, nullsFirst: false })
      .order("building", { ascending: true })
      .order("level", { ascending: true }),
    
    // Global order with formulas, options, plants, supplier
    supabase
      .from("global_orders")
      .select(`
        id,
        supplier_id,
        order_formulas (
          id, name, strength, exposure_class, consistency, granulometry, unit_price_m3
        ),
        order_supplier_options (
          id, name, category, unit_type, price, active
        ),
        global_order_plants (
          concrete_plants (
            id, supplier_id, city, address, email, phone
          )
        ),
        suppliers (
          id, name, email, phone, contact_name, plant_phone
        )
      `)
      .eq("site_id", siteId)
      .maybeSingle(),
  ]);

  // Process work items and extract buildings/levels
  const workItems = workItemsResult.data || [];
  const buildingsMap = new Map<string, Set<string>>();
  
  for (const item of workItems) {
    if (!buildingsMap.has(item.building)) {
      buildingsMap.set(item.building, new Set());
    }
    buildingsMap.get(item.building)!.add(item.level);
  }

  const buildings = Array.from(buildingsMap.entries()).map(([name, levels]) => ({
    name,
    levels: Array.from(levels).sort(),
  }));

  // Process global order data
  const globalOrder = globalOrderResult.data;
  const formulas = globalOrder?.order_formulas || [];
  const concreteOptions = globalOrder?.order_supplier_options || [];
  const plants = (globalOrder?.global_order_plants || [])
    .map((gop: any) => gop.concrete_plants)
    .filter(Boolean)
    .map((p: any) => ({
      id: p.id,
      supplier_id: p.supplier_id,
      name: p.city || 'Centrale', // Use city as name fallback
      city: p.city,
      address: p.address,
      email: p.email,
      phone: p.phone,
    }));
  const suppliers = globalOrder?.suppliers ? [globalOrder.suppliers] : [];

  return {
    site: siteResult.data || null,
    buildings,
    workItems: workItems.map((wi) => ({
      ...wi,
      formula_id: null,
    })),
    formulas,
    suppliers,
    plants,
    concreteOptions,
    workItemsCount: workItems.length,
    cached: false,
  };
}

// Static data query config - long cache, minimal refetching
const staticQueryConfig = {
  staleTime: 30 * 60 * 1000,      // 30 minutes - data considered fresh
  gcTime: 60 * 60 * 1000,         // 1 hour - keep in cache
  refetchOnMount: false as const, // Don't refetch on component mount
  refetchOnWindowFocus: false,    // Don't refetch on window focus
};

/**
 * Hook for static site data with long cache times
 * 
 * This data changes very rarely:
 * - Site info: almost never changes
 * - Formulas: rarely added/modified
 * - Suppliers: rarely change
 * - Work items structure: rarely modified
 * 
 * Using long staleTime and gcTime to minimize API calls
 * and share data across page navigations
 */
export function useSiteStaticData(siteId: string | undefined) {
  const query = useQuery({
    queryKey: ["site-static-data", siteId],
    queryFn: () => fetchSiteStaticDataFromEdge(siteId!),
    enabled: !!siteId,
    ...staticQueryConfig,
    // Use previous data during refetch
    placeholderData: (previousData) => previousData,
  });
  
  // Build lookup maps for O(1) access (memoized)
  const maps = useMemo(() => buildLookupMaps(query.data), [query.data]);
  
  return {
    ...query,
    maps,
  };
}

/**
 * Hook to prefetch static data for a site
 * Useful when navigating to a site detail page
 */
export function usePrefetchSiteStaticData() {
  const queryClient = useQueryClient();
  
  return {
    prefetch: (siteId: string) => {
      queryClient.prefetchQuery({
        queryKey: ["site-static-data", siteId],
        queryFn: () => fetchSiteStaticDataFromEdge(siteId),
        ...staticQueryConfig,
      });
    },
  };
}

// ========== LOOKUP HELPERS ==========

/**
 * Get a formula from the static data cache by ID
 */
export function useFormulaFromCache(formulaId: string | undefined, siteId: string | undefined) {
  const { data, maps } = useSiteStaticData(siteId);
  
  return useMemo(() => {
    if (!formulaId || !data) return null;
    return maps.formulasById.get(formulaId) || null;
  }, [formulaId, data, maps]);
}

/**
 * Get a work item from the static data cache by ID
 */
export function useWorkItemFromCache(workItemId: string | undefined, siteId: string | undefined) {
  const { data, maps } = useSiteStaticData(siteId);
  
  return useMemo(() => {
    if (!workItemId || !data) return null;
    return maps.workItemsById.get(workItemId) || null;
  }, [workItemId, data, maps]);
}

/**
 * Get a plant from the static data cache by ID
 */
export function usePlantFromCache(plantId: string | undefined, siteId: string | undefined) {
  const { data, maps } = useSiteStaticData(siteId);
  
  return useMemo(() => {
    if (!plantId || !data) return null;
    return maps.plantsById.get(plantId) || null;
  }, [plantId, data, maps]);
}

/**
 * Get a supplier from the static data cache by ID
 */
export function useSupplierFromCache(supplierId: string | undefined, siteId: string | undefined) {
  const { data, maps } = useSiteStaticData(siteId);
  
  return useMemo(() => {
    if (!supplierId || !data) return null;
    return maps.suppliersById.get(supplierId) || null;
  }, [supplierId, data, maps]);
}

// Export for external use
export { fetchSiteStaticDataFromEdge, fetchSiteStaticDataDirectly, buildLookupMaps };
